import { queryClient } from "queryClient";
import { resetAllStores } from "data/store/zustand-middeware";

const VERSION_KEY = "app_build_version";
const REDIRECT_PATH_KEY = "LOGIN_REDIRECT_PATH";

type VersionCheckResult = {
  isNewVersion: boolean;
  currentVersion: string | null;
  previousVersion: string | null;
};

const getCurrentVersion = (): string => {
  return import.meta.env.VITE_BUILD_VERSION;
};

const getStoredVersion = (): string | null => {
  try {
    return localStorage.getItem(VERSION_KEY);
  } catch (error) {
    console.error("Error reading stored version:", error);
    return null;
  }
};

const storeVersion = (version: string): void => {
  try {
    localStorage.setItem(VERSION_KEY, version);
  } catch (error) {
    console.error("Error storing version:", error);
  }
};

const deleteCookieForDomain = (
  name: string,
  domain: string,
  commonAttributes: string,
): void => {
  document.cookie = `${name}=; ${commonAttributes}; domain=${domain}`;
  document.cookie = `${name}=; ${commonAttributes}; Secure; domain=${domain}`;
};

const clearCookies = (): void => {
  const cookies = document.cookie.split(";");
  const expiryDate = "Thu, 01 Jan 1970 00:00:00 GMT";
  const commonAttributes = `expires=${expiryDate}; path=/; SameSite=Lax`;

  for (const cookie of cookies) {
    const eqPos = cookie.indexOf("=");
    const name = eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim();

    document.cookie = `${name}=; ${commonAttributes}`;
    document.cookie = `${name}=; ${commonAttributes}; Secure`;
    deleteCookieForDomain(name, globalThis.location.hostname, commonAttributes);

    const hostnameParts = globalThis.location.hostname.split(".");
    if (hostnameParts.length > 1) {
      for (let i = 0; i < hostnameParts.length - 1; i++) {
        const domain = `.${hostnameParts.slice(i).join(".")}`;
        deleteCookieForDomain(name, domain, commonAttributes);
      }
    }
  }
};

const clearLocalStoragePreservingCriticalKeys = (): void => {
  const version = localStorage.getItem(VERSION_KEY);
  const redirectPath = localStorage.getItem(REDIRECT_PATH_KEY);

  localStorage.clear();

  if (version) {
    localStorage.setItem(VERSION_KEY, version);
  }
  if (redirectPath) {
    localStorage.setItem(REDIRECT_PATH_KEY, redirectPath);
  }
};

const clearBrowserCaches = async (): Promise<void> => {
  if ("caches" in globalThis) {
    const cacheNames = await caches.keys();
    await Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName)));
  }
};

const unregisterServiceWorkers = async (): Promise<void> => {
  if ("serviceWorker" in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(
      registrations.map((registration) => registration.unregister()),
    );
  }
};

const clearAllCaches = async (): Promise<void> => {
  try {
    queryClient.clear();
    resetAllStores();
    clearLocalStoragePreservingCriticalKeys();
    sessionStorage.clear();
    clearCookies();
    await clearBrowserCaches();
    await unregisterServiceWorkers();
  } catch (error) {
    console.error("Error clearing caches:", error);
  }
};

const clearCachesAndReload = async (version: string): Promise<void> => {
  await clearAllCaches();
  storeVersion(version);
  await new Promise((resolve) => setTimeout(resolve, 100));
  globalThis.location.reload();
};

export const checkVersionAndClearCache =
  async (): Promise<VersionCheckResult> => {
    const currentVersion = getCurrentVersion();
    const previousVersion = getStoredVersion();

    const result: VersionCheckResult = {
      isNewVersion: false,
      currentVersion,
      previousVersion,
    };

    if (currentVersion === "development") {
      return result;
    }

    if (!previousVersion) {
      storeVersion(currentVersion);
      return result;
    }

    if (previousVersion !== currentVersion) {
      result.isNewVersion = true;
      await clearCachesAndReload(currentVersion);
    }

    return result;
  };

export const forceCacheClear = async (): Promise<void> => {
  await clearAllCaches();
  globalThis.location.reload();
};
